import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  IconButton,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomTypography from "../../globalStyles/fonts";
import dayjs from "dayjs";

const RoleDetailsModal = ({ role, open, onClose }) => {
  if (!role) return null;

  // Permission labels mapping
  const permissionLabels = {
    "user.create": "Create Users",
    "user.read": "View Users",
    "user.update": "Update Users",
    "user.delete": "Delete Users",
    "role.create": "Create Roles",
    "role.read": "View Roles",
    "role.update": "Update Roles",
    "role.delete": "Delete Roles",
    "order.create": "Create Orders",
    "order.read": "View Orders",
    "order.update": "Update Orders",
    "order.delete": "Delete Orders",
    "order.export": "Export Orders",
    "inventory.create": "Create Inventory",
    "inventory.read": "View Inventory",
    "inventory.update": "Update Inventory",
    "inventory.delete": "Delete Inventory",
    "client.create": "Create Clients",
    "client.read": "View Clients",
    "client.update": "Update Clients",
    "client.delete": "Delete Clients",
    "report.read": "View Reports",
    "report.export": "Export Reports",
    "report.analytics": "Analytics Access",
    "setting.read": "View Settings",
    "setting.update": "Update Settings",
    "setting.backup": "System Backup",
    "setting.create": "Create Settings",
    "setting.delete": "Delete Settings",
  };

  // Group permissions by module
  const groupedPermissions = {
    "User Management":
      role.permissions?.filter((p) => p.startsWith("user.")) || [],
    "Role Management":
      role.permissions?.filter((p) => p.startsWith("role.")) || [],
    "Order Management":
      role.permissions?.filter((p) => p.startsWith("order.")) || [],
    "Inventory Management":
      role.permissions?.filter((p) => p.startsWith("inventory.")) || [],
    "Client Management":
      role.permissions?.filter((p) => p.startsWith("client.")) || [],
    Reports: role.permissions?.filter((p) => p.startsWith("report.")) || [],
    "System Settings":
      role.permissions?.filter((p) => p.startsWith("setting.")) || [],
  };

  const DetailRow = ({ label, value, chipVariant = false }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{
            fontSize: 14,
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "var(--text-tertiary)",
            wordWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          {label}
        </CustomTypography>
      </Grid>
      <Grid item xs={12} md={8}>
        {chipVariant ? (
          <Chip
            label={value ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: value ? "#D7E9FF" : "#FFDEDF",
              color: value ? "#0070FF" : "#D13438",
              fontWeight: 500,
              fontSize: "12px",
            }}
          />
        ) : (
          <CustomTypography
            style={{
              fontSize: 14,
              fontFamily: "Poppins",
              fontWeight: 400,
              color: "#667085",
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: 1.5,
            }}
          >
            {value || "N/A"}
          </CustomTypography>
        )}
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="role-details-title"
      sx={{
        "& .MuiDialog-paper": {
          wordWrap: "break-word",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle
          id="role-details-title"
          sx={{
            fontFamily: "Poppins",
            fontSize: 24,
            fontWeight: 500,
            color: "var(--text-primary)",
            wordWrap: "break-word",
            whiteSpace: "normal",
          }}
        >
          Role Details
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 2 }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <DialogContent sx={{ wordWrap: "break-word", overflow: "auto" }}>
        <Grid container spacing={3}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Basic Information
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow label="Role Name" value={role?.name} />
              <DetailRow label="Description" value={role?.description} />
              <DetailRow
                label="Status"
                value={role?.is_active}
                chipVariant={true}
              />
              <DetailRow
                label="Total Permissions"
                value={`${role?.permissions?.length || 0} permissions`}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Permissions Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Permissions
            </Typography>

            {/* Permissions Summary */}
            <Box sx={{ mb: 3, pl: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  color: "#667085",
                  mb: 2,
                }}
              >
                This role has access to the following permissions:
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                {role?.permissions?.slice(0, 15).map((permission) => (
                  <Chip
                    key={permission}
                    label={permissionLabels[permission] || permission}
                    size="small"
                    sx={{
                      backgroundColor: "#E8F4FD",
                      color: "#0040FF",
                      fontSize: "11px",
                      fontFamily: "Poppins",
                    }}
                  />
                ))}
                {role?.permissions?.length > 15 && (
                  <Chip
                    label={`+${role.permissions.length - 15} more`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--bg-input)",
                      color: "#667085",
                      fontSize: "11px",
                      fontFamily: "Poppins",
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Permissions by Module */}
            {Object.entries(groupedPermissions).map(
              ([module, permissions]) =>
                permissions.length > 0 && (
                  <Accordion key={module} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: 16,
                            color: "var(--text-tertiary)",
                          }}
                        >
                          {module}
                        </Typography>
                        <Box sx={{ ml: "auto", mr: 2 }}>
                          <Chip
                            label={`${permissions.length} permission${
                              permissions.length > 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              backgroundColor: "#E8F4FD",
                              color: "#0040FF",
                              fontSize: "10px",
                              fontFamily: "Poppins",
                            }}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {permissions.map((permission) => (
                          <ListItem key={permission} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: 14,
                                    color: "var(--text-tertiary)",
                                  }}
                                >
                                  {permissionLabels[permission] || permission}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: 12,
                                    color: "#667085",
                                  }}
                                >
                                  {permission}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ),
            )}

            {(!role?.permissions || role.permissions.length === 0) && (
              <Box sx={{ pl: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 14,
                    color: "#667085",
                    fontStyle: "italic",
                  }}
                >
                  No permissions assigned to this role.
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Metadata Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Metadata
            </Typography>
            <Box sx={{ pl: 1 }}>
              <DetailRow
                label="Created By"
                value={
                  role?.created_by?.name ||
                  (role?.created_by?.first_name
                    ? `${role?.created_by?.first_name || ""} ${
                        role?.created_by?.last_name || ""
                      }`.trim()
                    : "N/A")
                }
              />
              <DetailRow
                label="Updated By"
                value={
                  role?.updated_by?.name ||
                  (role?.updated_by?.first_name
                    ? `${role?.updated_by?.first_name || ""} ${
                        role?.updated_by?.last_name || ""
                      }`.trim()
                    : "N/A")
                }
              />
              <DetailRow
                label="Created At"
                value={
                  role?.createdAt
                    ? dayjs(role.createdAt).format("MMMM DD, YYYY hh:mm A")
                    : "N/A"
                }
              />
              <DetailRow
                label="Updated At"
                value={
                  role?.updatedAt
                    ? dayjs(role.updatedAt).format("MMMM DD, YYYY hh:mm A")
                    : "N/A"
                }
              />
              <DetailRow label="Role ID" value={role?._id} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            minWidth: 100,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDetailsModal;
